import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";

type OrgTester = components["schemas"]["OrgTester"];
type OrgTesterListResponse = components["schemas"]["OrgTesterListResponse"];
type TesterAuthorizationStatus = components["schemas"]["TesterAuthorizationStatus"];
type InviteTesterRequest = components["schemas"]["InviteTesterRequest"];
type TesterInvite = components["schemas"]["TesterInvite"];
type TesterAuthorizationConflictError = components["schemas"]["TesterAuthorizationConflictError"];

// Cache windows follow the accounts canon: short list staleness so fresh
// writes appear quickly, with keepPreviousData smoothing pagination.
const LIST_STALE_TIME = 60_000;
const LIST_GC_TIME = 5 * 60_000;

/** Mirrors the listOrgTesters OpenAPI sort_by enum (the Go ORDER BY allow-list). */
export type TesterSortBy = "full_name" | "status" | "authorized_at" | "authorization_expires_at";

export interface TestersListParams {
  status?: TesterAuthorizationStatus;
  /**
   * Locked-out facet the single-value `status` param cannot express (the
   * suspended OR revoked union). When true the server ANDs it with `status`,
   * so a status outside that union lists no one.
   */
  locked_out?: boolean;
  testing_company_id?: string;
  /** ILIKE server-side search on the tester's name or email. */
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: TesterSortBy;
  sort_dir?: "asc" | "desc";
}

/**
 * Query-key factory for tester-authorization caches. Testers live on
 * org-param routes, so orgId threads through EVERY key including detail —
 * a detail key without orgId would serve stale cross-org rows after an
 * org switch.
 */
export const testerKeys = {
  all: ["testers"] as const,
  lists: () => [...testerKeys.all, "list"] as const,
  list: (orgId: string, params: TestersListParams) =>
    [...testerKeys.lists(), orgId, params] as const,
  detail: (orgId: string, id: string) => [...testerKeys.all, "detail", orgId, id] as const,
  invites: (orgId: string) => [...testerKeys.all, "invites", orgId] as const,
} as const;

function withDefaults(params: TestersListParams): TestersListParams {
  return {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
}

/**
 * List tester authorizations for the active org. Rows are keyed on the
 * org_testers row id (the authorization, NOT the profile) — re-authorization
 * after revoke creates a NEW row and history stays navigable.
 */
export function useTesters(params: TestersListParams = {}) {
  const orgId = useAppStore((s) => s.activeOrgId);
  const merged = withDefaults(params);
  return useQuery<OrgTesterListResponse>({
    queryKey: testerKeys.list(orgId ?? "", merged),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.GET("/orgs/{orgId}/testers", {
        params: { path: { orgId }, query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list testers";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Issue a tester invite — creation IS the invite under the utility-initiated
 * authorization model per AWWA M14 §10 administered-list practice: accepting
 * mints the org_testers row with status `active`. The 409 conflict envelope
 * (`tester_already_authorized` / `duplicate_pending_invite`) is threaded
 * through `ApiError.details` so the invite modal renders it inline, not as
 * a toast.
 */
export function useInviteTester() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: InviteTesterRequest) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST("/orgs/{orgId}/testers", {
        params: { path: { orgId } },
        body,
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to invite tester";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: testerKeys.invites(orgId) });
      }
      void queryClient.invalidateQueries({ queryKey: testerKeys.lists() });
    },
  });
}

export { LIST_STALE_TIME, LIST_GC_TIME };

export type {
  OrgTester,
  OrgTesterListResponse,
  TesterAuthorizationStatus,
  InviteTesterRequest,
  TesterInvite,
  TesterAuthorizationConflictError,
};
